// The rows as the page reads them (02-DESIGN/01-identity/01-tenants-and-roles.md
// § mapping rows): a row on a stable-subject claim, or a pending row on a
// handle, is a person; a row on any other claim — a role claim, a group, an
// organisation — is an access rule. The provider's configured stable-subject
// claim is not read for this; the three the designs name are.

import type { Row, RowPutRequest } from "../../wire/auth.ts";
import { Role } from "../../wire/identity.ts";

/** The stable-subject claims the designs name: `oid` on Entra, `sub` elsewhere, the numeric `id` on GitHub. */
export const IDENTITY_CLAIMS: ReadonlySet<string> = new Set(["sub", "oid", "id"]);

/** isPerson reports whether a row stands for one person rather than a rule. */
export function isPerson(r: Row): boolean {
  return r.pending === true || IDENTITY_CLAIMS.has(r.claim);
}

/** personName is what a person's row is called: the handle it was added by, or its value. */
export function personName(r: Row): string {
  return r.display !== undefined && r.display !== "" ? r.display : r.value;
}

/** splitRows divides the rows into the people and the access rules, each in the order given. */
export function splitRows(rows: readonly Row[]): { people: Row[]; rules: Row[] } {
  const people: Row[] = [];
  const rules: Row[] = [];
  for (const r of rows) {
    (isPerson(r) ? people : rules).push(r);
  }
  return { people, rules };
}

/**
 * roleChange is the put that gives a row a new role. The service keys a row
 * on its provider, claim and value, so a put of the same three replaces it:
 * the row goes back whole — still pending where it was, with the handle it
 * was added by — and only the role differs. The required mark is the
 * service's to set, never the caller's.
 */
export function roleChange(r: Row, role: Role): RowPutRequest {
  const row: Row = { ...r, role };
  delete row.required;
  return { row };
}

/** What lowering a row's role should be warned of: your own role, or the workspace's last admin row. */
export type RoleChangeWarning = "self" | "last-admin";

/**
 * roleChangeWarning says why lowering a row needs a second look. Nothing
 * guards the last admin (01-tenants-and-roles.md § the first admin): a
 * workspace whose admin rows are all gone is one the operator re-grants into,
 * so this warns and never refuses. `me` is the signed-in person's stable
 * subject; only a person's row can be theirs, never a rule's.
 */
export function roleChangeWarning(
  r: Row,
  role: Role,
  rows: readonly Row[],
  me: string,
): RoleChangeWarning | undefined {
  if (r.role !== Role.admin || role === Role.admin) {
    return undefined;
  }
  if (r.pending !== true && IDENTITY_CLAIMS.has(r.claim) && me !== "" && r.value === me) {
    return "self";
  }
  if (rows.filter((x) => x.role === Role.admin).length <= 1) {
    return "last-admin";
  }
  return undefined;
}

/**
 * byProvider splits the rows into those under the provider the workspace
 * signs in through and those kept under a provider not in use, which are
 * never matched and are what a switch back finds (01-the-console.md
 * § People under one provider; decision 0055).
 */
export function byProvider(rows: readonly Row[], inUse: string): { current: Row[]; kept: Row[] } {
  const current: Row[] = [];
  const kept: Row[] = [];
  for (const r of rows) {
    (r.provider === inUse ? current : kept).push(r);
  }
  return { current, kept };
}
